import _ from "lodash"

import {
    MDB_TYPES as TYPES,
    SELECTION_TYPES as ST
} from "@meddbriefer/scenario-data/constants.js"

const REC_TYPES_WITH_FINDINGS = [TYPES.ASSESS, TYPES.ASSESS_OPTION]
const REC_TYPES_WITH_OPTIONS = [TYPES.AWO, TYPES.DECISION]

const MED_DEFAULTS = {
    initialDosage: "",
    // repeatDosage: "",
    // repeatFrequency: "",
    // maxDosage: "",
    route: "",
    contraindications: [],
}

/*
 *  copies data from right to left but only if key exists in the left object in the
 *  first place (left defines schema, right clobbers old values)
 *  - only clobbers scalar values, you'll need to deal with arrays yourself and subobjects
 *  as separate calls
 */
function leftMergeObjects(left, right) {
    // console.log(left, right)
    const obj = _.cloneDeep(left)
    Object.keys(left).forEach(key => {
        if ("object" === typeof left[key] && null !== left[key]) {
            // skip all object children, noting that "null" is of type 'object'
            // console.log("skipping object key: " + key)
            // console.log(left[key])
        } else if (key in right) {
            // console.log(key, right[key])
            if (null !== right[key]) {
                obj[key] = right[key]
            } else {
                // console.log("not copying null value for " + key)
            }
        } else {
            // console.log(key + " not in right")
        }
    })
    return obj
}

const VITALS = [
    "BP",
    "P",
    "R",
    "Skin",
    "Spo2",
    "ETCO2",
    "GCS",
    "Glucose",
    "Pain",
    "Temp"
]

const DEFAULT_VITALS = {
    initial: "",
    good: "",
    bad: ""
}


function initializeScen(findings, checkListItemsWithOptions, meds) {
    const rec = {
        id: null,
        name: null,
        scenarioType: "trauma",
        schemaVersion: null,
        info: {
            debriefing: false,
            instructorInformation: "",
            dispatchInfo: "",
            maxTime: 20,
            sceneAssessment: "",
            patientInformation: {
                moulage: "",
                position: "",
                actions: "",
                age: 0,
                weight: 0,
                gender: ""
            }
        },
        SAMPLE: {
            S: "",
            A: "",
            M: "",
            P: "",
            L: "",
            E: ""
        },
        OPQRST: {
            O: "",
            P: "",
            Q: "",
            R: "",
            S: "",
            T: ""
        },
        vitalSigns: {},
        assessmentFindings: {},
        checkListCorrectness: {},
        correctMedicationDosages: {},
        problems: [],
        solutions: [],
        actions: [],
        interventionVariantAnswers: {}
    }
    VITALS.forEach(vital => {
        rec.vitalSigns[vital] = _.cloneDeep(DEFAULT_VITALS)
    })
    findings.forEach(finding => {
        rec.assessmentFindings[finding.id] = ""
    })
    checkListItemsWithOptions.forEach(cli => {
        rec.checkListCorrectness[cli.id] = null
    })
    meds.forEach(med => {
        rec.correctMedicationDosages[med.id] = _.cloneDeep(MED_DEFAULTS)
    })
    return rec
}


export const updateSchema = (scen, scenType) => {
    // track IDs added/deleted since last db save (if any)
    const additions = []
    const deletions = []
    // get records for things of various types
    const CLMD = scenType._checklist
    const MEDS = scenType._medications
    const INTVS = scenType._interventions
    const INTV_PROMPTS = scenType._interventionPrompts
    const INTV_PROMPT_ANSWERS = scenType._interventionPromptAnswers
    const FINDINGS = CLMD.filter(rec => REC_TYPES_WITH_FINDINGS.includes(rec.type))
    const CLI_W_OPTS = CLMD.filter(rec => REC_TYPES_WITH_OPTIONS.includes(rec.type))

    // create a scenario object initialized with proper fields for current schema
    // along with default values
    const newScen = initializeScen(FINDINGS, CLI_W_OPTS, MEDS)
    newScen.schemaVersion = scenType.schemaVersion
    if (scen.id) {
        newScen.id = scen.id
    }
    if (scen.name) {
        newScen.name = scen.name
    }
    if (scen.info) {
        newScen.info = leftMergeObjects(newScen.info, scen.info)
        if (scen.info.patientInformation) {
            newScen.info.patientInformation = leftMergeObjects(newScen.info.patientInformation,
                                                               scen.info.patientInformation)
        }
    }
    if (scen.SAMPLE) {
        newScen.SAMPLE = leftMergeObjects(newScen.SAMPLE, scen.SAMPLE)
    }
    if (scen.OPQRST) {
        newScen.OPQRST = leftMergeObjects(newScen.OPQRST, scen.OPQRST)
    }
    if (scen.vitalSigns) {
        Object.keys(newScen.vitalSigns).forEach(vital => {
            if (vital in scen.vitalSigns) {
                newScen.vitalSigns[vital] = leftMergeObjects(newScen.vitalSigns[vital],
                                                             scen.vitalSigns[vital])
            }
        })
    }
    if (scen.assessmentFindings) {
        newScen.assessmentFindings = leftMergeObjects(newScen.assessmentFindings,
                                                      scen.assessmentFindings)
        // find deleted findings ids
        Object.keys(scen.assessmentFindings).forEach(findingId => {
            const found = FINDINGS.find(rec => rec.id === findingId)
            if (!found) {
                deletions.push(findingId)
            }
        })
        // find new IDs
        FINDINGS.forEach(finding => {
            if (! (finding.id in scen.assessmentFindings)) {
                additions.push(finding)
            }
        })
    }
    if (scen.checkListCorrectness) {
        newScen.checkListCorrectness = leftMergeObjects(newScen.checkListCorrectness,
                                                        scen.checkListCorrectness)
        // find deletions
        Object.keys(scen.checkListCorrectness).forEach(itemId => {
            const found = CLI_W_OPTS.find(rec => rec.id === itemId)
            if (!found) {
                deletions.push(itemId)
            }
        })
        // find new IDs
        CLI_W_OPTS.forEach(cli => {
            if (! (cli.id in scen.checkListCorrectness)) {
                additions.push(cli)
            }
        })
    }
    if (scen.correctMedicationDosages) {
        MEDS.forEach(med => {
            if (med.id in scen.correctMedicationDosages) {
                newScen.correctMedicationDosages[med.id] = leftMergeObjects(newScen.correctMedicationDosages[med.id],
                                                                            scen.correctMedicationDosages[med.id])
            }
        })
    }

    // don't bother updating old schema - too much to change, keep problems, solutions,
    // and actions as empty lists if they don't exist as separate lists of objects
    if (undefined !== scen.actions && undefined !== scen.solutions) {
        scen.problems.forEach((prob, i) => {
            prob.assessments.forEach(rec => {
                // add new feedback field to assessments, if missing
                if (undefined === rec.feedback) {
                    rec.feedback = ""
                }
                // copy over assessIDs if they still exist, else mark
                // as deleted (they will probably show up as dups, perhaps
                // I should search within deletions for matching ID instead)
                if (CLMD[rec.assessmentID] === undefined) {
                    deletions.push(rec.assessmentID)
                }
            })
            newScen.problems.push(prob)
        })
        scen.solutions.forEach((sol, i) => {
            // add new field strictActionOrdering=false, if missing, to solutions
            if (undefined === sol.strictActionOrdering) {
                sol.strictActionOrdering = false
            }
            newScen.solutions.push(sol)
            // console.log("solution", sol)
        })
        scen.actions.forEach((act, i) => {
            if (Object.keys(act.interventionVariant).length === 0) {
                // push as-is
                newScen.actions.push(act)
            } else {
                // sanity check action interventionVariant first
                Object.entries(act.interventionVariant).forEach(([promptID, promptAnswers]) => {
                    const found = INTV_PROMPTS.find(e => e.id === promptID)
                    if (!found) {
                        //  handle error
                    } else {
                        const ansType = typeof promptAnswers
                        if (found.selectionType === ST.OOM) {
                            if ("string" === ansType) {
                                // string value which should be array of string value
                                act.interventionVariant[promptID] = [promptAnswers]
                            }
                        }
                    }
                })
                // push munged action
                newScen.actions.push(act)
            }
            if (!INTVS.find(e => e.id === act.interventionID)) {
                deletions.push(act.interventionID)
            }
        })
    }

    if (undefined === scen.interventionVariantAnswers) {
        // set to empty object so code below can simply iterate over nothing
        scen.interventionVariantAnswers = {}
    }
    Object.entries(scen.interventionVariantAnswers).forEach(([intvID, variantsObj]) => {
        const foundIntv = INTVS.find(e => e.id === intvID)
        if (!foundIntv) {
            deletions.push(intvID)
        } else {
            if (!(intvID in newScen.interventionVariantAnswers)) {
                newScen.interventionVariantAnswers[intvID] = {}
            }
            Object.entries(variantsObj).forEach(([variantJSON, variantData]) => {
                const variant = JSON.parse(variantJSON)
                let foundAll = true
                Object.entries(variant).forEach(([promptID, answerID]) => {
                    const foundPrompt = INTV_PROMPTS.find(e => e.id === promptID)
                    if (!foundPrompt) {
                        foundAll = false
                        deletions.push(promptID)
                    } else {
                        const foundAnswer = INTV_PROMPT_ANSWERS.find(e => e.id === answerID)
                        if (!foundAnswer) {
                            deletions.push(answerID)
                        }
                    }
                })
                if (foundAll) {
                    newScen.interventionVariantAnswers[intvID][variantJSON] = {}
                    // get list of currrent promptIDs for intv
                    let promptIDs = new Set(INTV_PROMPTS.filter(e => e.interventionID === intvID)
                                                         .map(e => e.id))
                    Object.entries(scen.interventionVariantAnswers[intvID][variantJSON])
                            .forEach(([promptID, ansIDs]) => {
                        promptIDs.delete(promptID)
                        const foundPrompt = INTV_PROMPTS.find(e => e.id === promptID)
                        if (!foundPrompt) {
                            deletions.push(promptID)
                        } else {
                            let foundAll = true
                            if ("string" === typeof ansIDs) {
                                const foundAnswer = INTV_PROMPT_ANSWERS.find(e => e.id === ansIDs)
                                if (!foundAnswer) {
                                    foundAll = false
                                    deletions.push(ansIDs)
                                }
                            } else {
                                ansIDs.forEach(ansID => {
                                    const foundAnswer = INTV_PROMPT_ANSWERS.find(e => e.id === ansID)
                                    if (!foundAnswer) {
                                        foundAll = false
                                        deletions.push(ansID)
                                    }
                                })
                            }
                            if (foundAll) {
                                newScen.interventionVariantAnswers[intvID][variantJSON][promptID] = ansIDs
                            }
                        }
                    })
                    if (promptIDs.size > 0) {
                        Array.from(promptIDs).forEach(promptID => {
                            // set any missing promptIDs to have no assigned answers
                            newScen.interventionVariantAnswers[intvID][variantJSON][promptID] = []
                        })
                    }
                }
            })
        }
    })

    console.log("additions", additions)
    console.log("deletions", deletions)
    // console.log("AFTER", newScen)
    return newScen
}
